/* Gerber collection browse: progressive enhancement for the filter form.
   Without JS the form submits normally (full page load). With JS it fetches the same URL through the
   Section Rendering API (?section_id=) and swaps the section content, keeping URL, history and focus sane. */
class GerberCollectionBrowse extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.setAttribute('data-js', '');
    this.debounce = null;
    this.onChange = (e) => this.handleChange(e);
    this.onSubmit = (e) => this.handleSubmit(e);
    this.onClick = (e) => this.handleClick(e);
    this.onInput = (e) => this.handleInput(e);
    this.onKey = (e) => {
      if (e.key === 'Escape') this.closeDrawer(true);
    };
    this.onPop = () => this.load(location.pathname + location.search, false);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.addEventListener('keydown', this.onKey);
    window.addEventListener('popstate', this.onPop);
    this.measureSort();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.measureSort());
  }

  /* The dropdown fills the whole sort box; its text starts after the "Sort by:" label, so the label's width is measured. */
  measureSort() {
    this.querySelectorAll('.g-cbr__sort-box').forEach((box) => {
      const label = box.querySelector('.g-cbr__sort-label');
      if (label) box.style.setProperty('--gcb-sort-label-w', Math.ceil(label.getBoundingClientRect().width) + 'px');
    });
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPop);
    document.body.classList.remove('g-cbr-lock');
    clearTimeout(this.debounce);
  }

  get side() {
    return this.querySelector('[data-side]');
  }

  get form() {
    return this.querySelector('[data-form]');
  }

  handleChange(e) {
    const t = e.target;
    if (t.matches('[data-sort]')) {
      this.submitForm();
    } else if (this.form && this.form.contains(t) && !t.matches('[data-search]')) {
      if (this.dataset.autoApply === 'true') {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.submitForm(), 350);
      }
    }
  }

  handleSubmit(e) {
    if (e.target.matches('[data-form]')) {
      e.preventDefault();
      this.submitForm();
    }
  }

  handleClick(e) {
    const link = e.target.closest('a[data-ajax]');
    if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      this.load(link.getAttribute('href'), true, true);
      return;
    }
    if (e.target.closest('[data-open]')) this.openDrawer();
    else if (e.target.closest('[data-close]') || e.target.matches('[data-overlay]')) this.closeDrawer(true);
  }

  handleInput(e) {
    if (!e.target.matches('[data-search]')) return;
    const q = e.target.value.trim().toLowerCase();
    e.target.closest('.g-cbr__group-body').querySelectorAll('[data-label]').forEach((li) => {
      li.hidden = q !== '' && li.dataset.label.indexOf(q) === -1;
    });
  }

  openDrawer() {
    const side = this.side;
    if (!side) return;
    side.classList.add('is-open');
    const overlay = this.querySelector('[data-overlay]');
    if (overlay) overlay.hidden = false;
    document.body.classList.add('g-cbr-lock');
    const btn = this.querySelector('[data-open]');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    const first = side.querySelector('summary, input, a, button');
    if (first) first.focus();
  }

  closeDrawer(restoreFocus) {
    const side = this.side;
    if (!side || !side.classList.contains('is-open')) return;
    side.classList.remove('is-open');
    const overlay = this.querySelector('[data-overlay]');
    if (overlay) overlay.hidden = true;
    document.body.classList.remove('g-cbr-lock');
    const btn = this.querySelector('[data-open]');
    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
      if (restoreFocus) btn.focus();
    }
  }

  /* Build the URL from the form (filters + sort) and load it. */
  submitForm() {
    const form = this.form;
    const params = new URLSearchParams();
    if (form) {
      new FormData(form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
    }
    const sortEl = this.querySelector('[data-sort]');
    if (sortEl && sortEl.value && !params.has('sort_by')) params.append('sort_by', sortEl.value);
    const qs = params.toString();
    this.load(location.pathname + (qs ? '?' + qs : ''), true);
  }

  async load(url, push = true, scroll = false) {
    const fallback = () => {
      window.location.href = url;
    };
    this.classList.add('is-loading');
    const openGroups = {};
    this.querySelectorAll('[data-group]').forEach((d) => {
      openGroups[d.dataset.group] = d.open;
    });
    const drawerOpen = !!(this.side && this.side.classList.contains('is-open'));
    const activeId = document.activeElement && document.activeElement.id;
    try {
      const target = new URL(url, location.origin);
      target.searchParams.set('section_id', this.sectionId);
      const res = await fetch(target.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) return fallback();
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = doc.querySelector('gerber-collection-browse');
      if (!next) return fallback();
      this.innerHTML = next.innerHTML;
      this.measureSort();
      this.style.cssText = next.getAttribute('style') || this.style.cssText;
      this.querySelectorAll('[data-group]').forEach((d) => {
        if (d.dataset.group in openGroups) d.open = openGroups[d.dataset.group];
      });
      if (drawerOpen) this.openDrawer();
      if (push) {
        const clean = new URL(url, location.origin);
        clean.searchParams.delete('section_id');
        history.pushState({}, '', clean.pathname + clean.search);
      }
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = status.textContent.trim();
      if (activeId) {
        const el = document.getElementById(activeId);
        if (el) el.focus({ preventScroll: true });
      }
      if (scroll) {
        const top = this.querySelector('[data-results]').getBoundingClientRect().top + window.scrollY - 24;
        window.scrollTo({ top: Math.max(0, Math.min(top, window.scrollY)), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    } catch (err) {
      fallback();
    } finally {
      this.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('gerber-collection-browse')) customElements.define('gerber-collection-browse', GerberCollectionBrowse);
